import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { BankAccount, BankAccountFormData } from '@/types';
import { useAuth } from '../AuthContext';
import { useProfile } from '../ProfileContext';
import { useEncryption } from '../EncryptionContext';
import { useSubscription } from '../SubscriptionContext';
import { AccountsContextType } from '@/types/contexts';
import { loadAccountsFromSupabase } from '@/lib/supabase-operations';
import { accountsReducer, initialState } from './reducer';
import { useAccountSelectors } from './selectors';
import { 
  addAccountOperation, 
  updateAccountOperation, 
  updateAccountBalanceOperation,
  deleteAccountOperation 
} from './operations';
import { logger } from '@/utils/logger';

const AccountsContext = createContext<AccountsContextType | undefined>(undefined);

export const AccountsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(accountsReducer, initialState);
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { planLimits } = useSubscription();
  const { isEncryptionReady, isCheckingEncryption } = useEncryption();

  const selectors = useAccountSelectors(state.accounts);

  // Load accounts from Supabase
  useEffect(() => {
    if (!user) return;

    if (isCheckingEncryption) {
      logger.debug('Waiting for encryption to be ready...');
      return;
    }
    
    if (!isEncryptionReady) {
      logger.warn('Encryption not ready, skipping load');
      dispatch({ type: 'CLEAR_ACCOUNTS' });
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    const loadAccounts = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        logger.info('Loading accounts from Supabase');
        const supabaseAccounts = await loadAccountsFromSupabase();
        dispatch({ type: 'SET_ACCOUNTS', payload: supabaseAccounts });
        logger.info('Accounts loaded successfully', { count: supabaseAccounts.length });
      } catch (error) {
        logger.error('Error loading accounts', { error });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadAccounts();
  }, [user, isEncryptionReady, isCheckingEncryption]);

  // Reload when active profile changes
  useEffect(() => {
    if (user && activeProfile) {
      if (isCheckingEncryption) {
        logger.debug('Waiting for encryption before profile reload...');
        return;
      }
      
      if (!isEncryptionReady) {
        logger.warn('Encryption not ready for profile reload');
        return;
      }

      logger.debug('Profile changed, reloading accounts', { profileId: activeProfile.id });
      
      const loadAccounts = async () => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
          const supabaseAccounts = await loadAccountsFromSupabase();
          dispatch({ type: 'SET_ACCOUNTS', payload: supabaseAccounts });
          logger.info('Accounts reloaded for profile', { 
            profileName: activeProfile.name, 
            count: supabaseAccounts.length 
          });
        } catch (error) {
          logger.error('Error reloading accounts', { error });
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      };
      loadAccounts();
    }
  }, [activeProfile?.id, user, isEncryptionReady, isCheckingEncryption]);

  const addAccount = useCallback(async (account: BankAccountFormData): Promise<BankAccount> => {
    return addAccountOperation(account, dispatch, planLimits.maxAccounts, state.accounts.length);
  }, [planLimits.maxAccounts, state.accounts.length]);

  const updateAccount = useCallback(async (id: string, updates: Partial<BankAccount>) => {
    return updateAccountOperation(id, updates, state.accounts, dispatch);
  }, [state.accounts]);

  const updateAccountBalance = useCallback(async (accountId: string, amount: number) => {
    return updateAccountBalanceOperation(accountId, amount, state.accounts, dispatch);
  }, [state.accounts]);

  const deleteAccount = useCallback(async (id: string) => {
    return deleteAccountOperation(id, dispatch);
  }, []);

  const value: AccountsContextType = useMemo(() => ({
    accounts: state.accounts,
    isLoading: state.isLoading,
    error: null,
    addAccount,
    updateAccount,
    updateAccountBalance,
    deleteAccount,
    getAccountById: selectors.getById,
  }), [
    state.accounts, 
    state.isLoading, 
    addAccount, 
    updateAccount, 
    updateAccountBalance, 
    deleteAccount,
    selectors
  ]);

  return (
    <AccountsContext.Provider value={value}>
      {children}
    </AccountsContext.Provider>
  );
};

export const useAccounts = () => {
  const context = useContext(AccountsContext);
  if (!context) {
    throw new Error('useAccounts must be used within an AccountsProvider');
  }
  return context;
};
